import {X} from 'lucide-react';
import * as React from 'react';
import InputGroup from 'src/InputGroup';
import {TextInput} from 'src/inputs/TextInput';
import {LogoImage} from 'src/LogoImage';
import {LOGO_OPTIONS, type LogoOption} from 'src/logoOptions';
import {Popover} from 'src/Popover/NewPopover';
import {PopoverContents, PopoverItem} from 'src/Popover/views';
import {useSearchPopover} from 'src/SearchPopover/useSearchPopover';
import {useSearch} from 'src/useSearch';
import type {FocusControls} from 'src/FocusTrap';
import type {SubmitState} from 'src/SearchPopover/useSearchPopover';

// The "None" entry is handled by the clear button rather than as a searchable
// row — an empty option in a typeahead list is confusing to scroll past.
const SELECTABLE = LOGO_OPTIONS.filter((option) => option.value !== '');

const RESULT_LIMIT = 60;

const labelFor = (value: string): string =>
  LOGO_OPTIONS.find((option) => option.value === value)?.label ?? value;

type Props = {
  value: string;
  onChange: (logo: string) => void;
  /** Omit to render bare, without the "Logo" InputGroup wrapper. */
  label?: string | null;
  /**
   * Accessible name for the input. Required when `label` is null, which
   * removes the only visible label — in a list of rows every picker would
   * otherwise be announced as the same "Search logos…" with nothing tying it
   * to its payee.
   */
  ariaLabel?: string;
  autoFocus?: boolean;
  placeholder?: string;
  /** Blocks interaction while an assignment is in flight. */
  disabled?: boolean;
};

/**
 * Searchable logo picker showing a thumbnail beside every option.
 *
 * This replaced a native <select>, which stopped being usable once there were
 * 200 logos: a browser cannot render an image inside an <option>, so the list
 * was text-only and had no filtering. Search covers both the label and the slug
 * so "bofa" finds Bank of America.
 */
export const LogoSelect = ({
  value,
  onChange,
  label = 'Logo',
  ariaLabel,
  autoFocus,
  placeholder = 'Search logos…',
  disabled,
}: Props) => {
  // Stable ids so the input can point at the listbox and the active option.
  const listboxId = React.useId();
  const optionId = (index: number) => `${listboxId}-option-${index}`;

  const activeItemRef = React.useRef<HTMLDivElement | null>(null);
  const anchorRef = React.useRef<HTMLInputElement | null>(null);
  const popoverRef = React.useRef<HTMLDivElement | null>(null);
  const focusControlsRef = React.useRef<FocusControls | null>(null);

  const {search} = useSearch(SELECTABLE, ['label', 'value']);

  // useSearch returns every item verbatim for an empty query, ignoring the Fuse
  // limit, so an untouched picker would mount all 200 options and fetch the
  // whole ~1.6MB logo directory. Cap it here rather than relying on the limit.
  const runSearch = (value: string): LogoOption[] =>
    value.trim() === ''
      ? SELECTABLE.slice(0, RESULT_LIMIT)
      : search(value, {limit: RESULT_LIMIT});

  const handleSubmit = (state: SubmitState<LogoOption>) => {
    if (state.selection) onChange(state.selection.value);
    // Clear before closing, not after: handleQueryChange also sets isOpen, so
    // resetting the query last would immediately reopen the popover.
    handleQueryChange('');
    closePopover();
    // Tab sets focusDirection and useSearchPopover has already preventDefault-ed
    // the native move, so focus would be stranded on this input unless we move
    // it ourselves. Enter and clicks leave focus alone deliberately — in the
    // settings list each row has its own picker, and jumping to the next row's
    // input after a click would be surprising.
    if (state.focusDirection === 'backward') {
      focusControlsRef.current?.focusBackward();
    } else if (state.focusDirection === 'forward') {
      focusControlsRef.current?.focusForward();
    }
  };

  const handleClear = () => {
    onChange('');
    handleQueryChange('');
    closePopover();
  };

  const {
    isOpen,
    query,
    searchResults,
    selectedIndex,
    handleKeyDown,
    handleQueryChange,
    openPopover,
    closePopover,
  } = useSearchPopover<LogoOption>({
    initialQuery: '',
    focusControlsRef,
    search: runSearch,
    onSubmit: handleSubmit,
  });

  React.useLayoutEffect(() => {
    if (isOpen && anchorRef.current && popoverRef.current) {
      popoverRef.current.style.width = `${anchorRef.current.offsetWidth}px`;
    }
  }, [anchorRef.current, popoverRef.current, isOpen]);

  React.useLayoutEffect(() => {
    activeItemRef.current?.scrollIntoView({block: 'nearest'});
  }, [selectedIndex]);

  const picker = (
    <div className="flex items-center gap-2">
      <LogoImage logo={value} className="size-8" alt={value} />
      <div className="relative flex-1">
        <TextInput
          ref={anchorRef}
          value={query}
          onChange={handleQueryChange}
          onClick={openPopover}
          onFocus={openPopover}
          onKeyDown={handleKeyDown}
          autoFocus={autoFocus}
          selectOnFocus
          disabled={disabled}
          aria-label={ariaLabel ?? (typeof label === 'string' ? label : undefined)}
          // Combobox wiring. The native <select> this replaced announced its
          // expanded state and active option for free; a bare text input plus
          // styled divs gives assistive tech nothing, so arrow keys would only
          // change colours as far as a screen reader is concerned.
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={
            isOpen && selectedIndex !== null ? optionId(selectedIndex) : undefined
          }
          placeholder={value ? labelFor(value) : placeholder}
        />
      </div>
      {value && (
        <button
          type="button"
          disabled={disabled}
          onClick={handleClear}
          className="text-muted-foreground hover:text-foreground shrink-0 p-1"
          aria-label={ariaLabel ? `Clear ${ariaLabel}` : 'Clear logo'}
        >
          <X className="size-4" />
        </button>
      )}
      <Popover
        anchorRef={anchorRef}
        anchor="bottom-left"
        focusControlsRef={focusControlsRef}
        open={isOpen}
        onOutsideClick={closePopover}
        onOutsideFocus={closePopover}
        passThroughOutsideClicks
      >
        <PopoverContents ref={popoverRef} id={listboxId} role="listbox">
          {searchResults.length === 0 ? (
            <div className="text-muted-foreground px-3 py-2">No logos match</div>
          ) : (
            searchResults.map((option, index) => (
              <PopoverItem
                key={option.value}
                id={optionId(index)}
                role="option"
                aria-selected={selectedIndex === index}
                isSelected={selectedIndex === index}
                ref={selectedIndex === index ? activeItemRef : null}
                onClick={() => handleSubmit({query, selection: option})}
              >
                <div className="flex items-center gap-2">
                  <LogoImage logo={option.value} className="size-5" />
                  <span className="truncate">{option.label}</span>
                </div>
              </PopoverItem>
            ))
          )}
        </PopoverContents>
      </Popover>
    </div>
  );

  return label === null ? picker : <InputGroup label={label}>{picker}</InputGroup>;
};
